import { z } from "zod";
import {
  Permission,
  PlayerSearchQuery,
  ProfileId,
  ProfileSearchQuery,
  ProfileSummary,
  PublicProfile,
  Timestamp,
  UserId,
  Username,
  UsernameFormatCode,
} from "../../common";
import { ProfileModeration } from "../../moderation/v1alpha1/schemas";

const EXAMPLE_FOLDER = "https://paxusercontent.co/profiles/0199f0be-4d1a-7b55-8c71-59a0d2e6c884";

const PROFILE_EXAMPLE = {
  profileId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
  username: "Grace_Hopper",
  displayName: "Grace Hopper",
  avatar: {
    sources: [64, 128, 256, 512].map(size => ({
      url: `${EXAMPLE_FOLDER}/avatar/k3v9x2mq7tfa/${size}.avif`,
      width: size,
      height: size,
    })),
  },
  banner: {
    sources: [750, 1500, 3000].map(width => ({
      url: `${EXAMPLE_FOLDER}/banner/w4n8c2r6y5ph/${width}.avif`,
      width,
      height: width / 3,
    })),
  },
  bio: "Computer pioneer and naval officer.",
  region: "New York, United States",
  friendCount: 12,
  lastActiveAt: "2026-09-18T20:12:00.000Z",
  createdAt: "2026-09-04T17:00:00.000Z",
  updatedAt: "2026-09-18T20:10:00.000Z",
} as const;

const UPLOAD_EXAMPLE = {
  uploadId: "k3v9x2mq7tfa",
  url: "https://example.r2.cloudflarestorage.com/pax-uploads/profiles/0199f0be-4d1a-7b55-8c71-59a0d2e6c884/avatar/k3v9x2mq7tfa?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Expires=300&X-Amz-SignedHeaders=content-length%3Bcontent-type%3Bhost&X-Amz-Signature=…",
  expiresAt: "2026-09-18T20:15:00.000Z",
} as const;

export const IMAGE_UPLOAD_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

/** Small enough that SafeSearch can scan the original in one inline request. */
export const IMAGE_UPLOAD_MAX_BYTES = 7 * 1024 * 1024;

export const ProfileIdParams = z
  .strictObject({
    profileId: ProfileId,
  })
  .meta({
    id: "ProfileIdParams",
    description: "A route parameter naming one public profile.",
    example: { profileId: PROFILE_EXAMPLE.profileId },
  });
export type ProfileIdParams = z.infer<typeof ProfileIdParams>;

export const ProfileResponse = z
  .strictObject({
    profile: PublicProfile,
    moderation: ProfileModeration.optional().meta({
      description: "The profile's open appeals. Returned only to the profile's owner.",
    }),
  })
  .meta({
    id: "ProfileResponse",
    description: "One public profile.",
    example: { profile: PROFILE_EXAMPLE },
  });
export type ProfileResponse = z.infer<typeof ProfileResponse>;

export const ProfileStatsResponse = z
  .strictObject({
    roundsPlayed: z.number().int().nonnegative().meta({
      description: "Rounds completed in played games while this profile was seated.",
      example: 148,
    }),
    playSeconds: z.number().int().nonnegative().meta({
      description: "Seconds spent engaged with Pax: in a live game, or with recent input.",
      example: 86_400,
    }),
  })
  .meta({
    id: "ProfileStatsResponse",
    description: "A profile's play totals, read from ClickHouse's daily rollups.",
    example: { roundsPlayed: 148, playSeconds: 86_400 },
  });
export type ProfileStatsResponse = z.infer<typeof ProfileStatsResponse>;

export const UpdateProfileRequest = z
  .strictObject({
    displayName: z.string().trim().min(1).max(50).optional().meta({
      description: "The public display name. Omit to leave it unchanged.",
      example: PROFILE_EXAMPLE.displayName,
    }),
    bio: z.string().trim().max(500).nullable().optional().meta({
      description: "The public biography, or null to remove it.",
      example: PROFILE_EXAMPLE.bio,
    }),
    region: z.string().trim().max(100).nullable().optional().meta({
      description: "Public location text, or null to remove it.",
      example: PROFILE_EXAMPLE.region,
    }),
  })
  .refine(body => Object.values(body).some(value => value !== undefined), {
    message: "At least one profile field is required.",
  })
  .meta({
    id: "UpdateProfileRequest",
    description:
      "Public profile fields to replace. Omitted fields stay unchanged; nullable fields are cleared with null.",
    example: {
      displayName: PROFILE_EXAMPLE.displayName,
      bio: PROFILE_EXAMPLE.bio,
      region: PROFILE_EXAMPLE.region,
    },
  });
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequest>;

export const ProfileImageKindParams = z.strictObject({
  profileId: ProfileId,
  kind: z.enum(["avatar", "banner"]),
});
export type ProfileImageKindParams = z.infer<typeof ProfileImageKindParams>;

export const UploadId = z
  .string()
  .regex(/^[0-9a-z]{12}$/)
  .brand<"UploadId">()
  .meta({
    id: "UploadId",
    description:
      "The id of one image upload, 12 lowercase letters and digits. Send it to set the image once the upload finishes.",
    example: UPLOAD_EXAMPLE.uploadId,
  });
export type UploadId = z.infer<typeof UploadId>;

export const CreateImageUploadRequest = z
  .strictObject({
    contentType: z.enum(IMAGE_UPLOAD_CONTENT_TYPES).meta({
      description: "The image's type. The upload URL accepts only this type.",
      example: "image/jpeg",
    }),
    bytes: z.int().min(1).max(IMAGE_UPLOAD_MAX_BYTES).meta({
      description:
        "The image's size in bytes, at most 7 MiB. The upload URL accepts only a file of exactly this size.",
      example: 3_482_113,
    }),
  })
  .meta({
    id: "CreateImageUploadRequest",
    description: "The image the client is about to upload.",
    example: { contentType: "image/jpeg", bytes: 3_482_113 },
  });
export type CreateImageUploadRequest = z.infer<typeof CreateImageUploadRequest>;

export const ImageUpload = z
  .strictObject({
    url: z.url().meta({
      description: "A presigned storage URL. Send the image's bytes to it as the request body.",
      example: UPLOAD_EXAMPLE.url,
    }),
    method: z.literal("PUT"),
    headers: z
      .strictObject({
        "Content-Type": z.enum(IMAGE_UPLOAD_CONTENT_TYPES).meta({ example: "image/jpeg" }),
      })
      .meta({
        description:
          "Send exactly these headers. The URL is signed for this content type and for the size in the request, which the client sends as `Content-Length` on its own.",
      }),
    expiresAt: Timestamp.meta({
      description:
        "The URL stops accepting the upload at this time, 5 minutes after it was issued.",
      example: UPLOAD_EXAMPLE.expiresAt,
    }),
  })
  .meta({ id: "ImageUpload", description: "Where and how to upload the image." });
export type ImageUpload = z.infer<typeof ImageUpload>;

export const CreateImageUploadResponse = z
  .strictObject({
    uploadId: UploadId,
    upload: ImageUpload,
  })
  .meta({
    id: "CreateImageUploadResponse",
    description: "The upload's id, and where to upload the image.",
    example: {
      uploadId: UPLOAD_EXAMPLE.uploadId,
      upload: {
        url: UPLOAD_EXAMPLE.url,
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        expiresAt: UPLOAD_EXAMPLE.expiresAt,
      },
    },
  });
export type CreateImageUploadResponse = z.infer<typeof CreateImageUploadResponse>;

export const ImageCrop = z
  .strictObject({
    x: z.number().min(0).max(1).meta({
      description: "The crop's centre across the image, from 0 at the left edge to 1 at the right.",
      example: 0.5,
    }),
    y: z.number().min(0).max(1).meta({
      description: "The crop's centre down the image, from 0 at the top edge to 1 at the bottom.",
      example: 0.4,
    }),
    zoom: z.number().min(1).max(5).optional().meta({
      description:
        "How far the crop zooms in, from 1 to 5. At 1 it is the largest square (avatar) or 3:1 strip (banner) that fits in the image; at 2 it is half that width and height. Defaults to 1.",
      example: 1.5,
    }),
  })
  .meta({
    id: "ImageCrop",
    description:
      "The part of the upright image to keep. Near an edge, the crop moves inside the image rather than centring on the point.",
    example: { x: 0.5, y: 0.4, zoom: 1.5 },
  });
export type ImageCrop = z.infer<typeof ImageCrop>;

export const SetProfileImageRequest = z
  .strictObject({
    uploadId: UploadId.meta({
      description: "The upload holding the image, for the same kind of image.",
      example: UPLOAD_EXAMPLE.uploadId,
    }),
    crop: ImageCrop.optional().meta({
      description:
        "The part of the image to keep. Without it, the crop keeps the region most likely to draw the eye.",
    }),
  })
  .meta({
    id: "SetProfileImageRequest",
    description: "The uploaded image to show as the avatar or banner, and how to crop it.",
    example: { uploadId: UPLOAD_EXAMPLE.uploadId, crop: { x: 0.5, y: 0.4, zoom: 1.5 } },
  });
export type SetProfileImageRequest = z.infer<typeof SetProfileImageRequest>;

export const ImageRejection = z
  .strictObject({
    reason: z
      .enum(["unsupported_format", "too_large", "too_long", "decode_failed", "explicit_content"])
      .meta({
        description:
          "Why the image can't be used. `unsupported_format`: the file isn't a JPEG, PNG, WebP, or GIF. `too_large`: the file is over 7 MiB or 40 megapixels, or an animation has too many frames. `too_long`: an animation runs over 15 seconds. `decode_failed`: the file is damaged. `explicit_content`: Google's SafeSearch rates it as likely sexually explicit; it is refused before automod, isn't stored, and can't be appealed.",
        example: "unsupported_format",
      }),
  })
  .meta({
    id: "ImageRejection",
    description: "Why the uploaded image can't be used.",
    example: { reason: "unsupported_format" },
  });
export type ImageRejection = z.infer<typeof ImageRejection>;

export const SetUsernameRequest = z
  .strictObject({
    username: Username,
  })
  .meta({
    id: "SetUsernameRequest",
    description: "The username to claim or change to.",
    example: { username: PROFILE_EXAMPLE.username },
  });
export type SetUsernameRequest = z.infer<typeof SetUsernameRequest>;

const RenameAvailability = {
  renamesRemaining: z.number().int().min(0).max(2).meta({
    description: "How many username renames remain in the rolling previous 90 days.",
    example: 2,
  }),
  nextRenameAvailableAt: Timestamp.nullable().meta({
    description:
      "When the next rename becomes available after reaching the limit, or null while a rename is available.",
    example: null,
  }),
};

export const SetUsernameResponse = z
  .strictObject({
    profile: PublicProfile,
    ...RenameAvailability,
  })
  .meta({
    id: "SetUsernameResponse",
    description: "The updated profile and its current username rename allowance.",
    example: {
      profile: PROFILE_EXAMPLE,
      renamesRemaining: 2,
      nextRenameAvailableAt: null,
    },
  });
export type SetUsernameResponse = z.infer<typeof SetUsernameResponse>;

export const CheckUsernameAvailabilityQuery = z
  .strictObject({
    username: z.string().default("").meta({
      description:
        "The raw text to check. Missing or invalid text yields a name status of invalid with a format code.",
      example: PROFILE_EXAMPLE.username,
    }),
  })
  .meta({
    id: "CheckUsernameAvailabilityQuery",
    description: "Raw username input for an availability check.",
  });
export type CheckUsernameAvailabilityQuery = z.infer<typeof CheckUsernameAvailabilityQuery>;

export const UsernameNameResult = z
  .discriminatedUnion("status", [
    z.strictObject({
      status: z.literal("available").meta({
        description: "No profile owns the normalized username.",
      }),
    }),
    z.strictObject({
      status: z.literal("current").meta({
        description:
          "The caller already owns this normalized username. Sending it changes at most the display casing and consumes no rename.",
      }),
    }),
    z.strictObject({
      status: z.literal("taken").meta({
        description: "Another profile owns the normalized username.",
      }),
    }),
    z.strictObject({
      status: z.literal("invalid").meta({
        description: "The input breaks a username format rule.",
      }),
      formatCode: UsernameFormatCode.meta({
        description: "The first username format rule the input breaks.",
        example: "must_start_with_letter",
      }),
    }),
  ])
  .meta({
    id: "UsernameNameResult",
    description:
      "What the check found out about the name itself, independent of the caller's rename allowance.",
    example: { status: "available" },
  });
export type UsernameNameResult = z.infer<typeof UsernameNameResult>;

export const CheckUsernameAvailabilityResponse = z
  .strictObject({
    name: UsernameNameResult,
    ...RenameAvailability,
    canSubmit: z.boolean().meta({
      description:
        "Whether sending this exact username to the username endpoint would succeed right now: the name is available or current, and either this is the profile's first claim, the name is current, or a rename remains.",
      example: true,
    }),
  })
  .meta({
    id: "CheckUsernameAvailabilityResponse",
    description:
      "Two independent answers: what is true of the name, and what is true of the caller's rename allowance. Render them as separate form messages.",
    example: {
      name: { status: "available" },
      renamesRemaining: 2,
      nextRenameAvailableAt: null,
      canSubmit: true,
    },
  });
export type CheckUsernameAvailabilityResponse = z.infer<typeof CheckUsernameAvailabilityResponse>;

export const SearchProfilesQuery = ProfileSearchQuery.meta({
  id: "SearchProfilesQuery",
  description: "A small profile search by exact profile id or username prefix.",
  example: { q: "gra", limit: 10 },
});
export type SearchProfilesQuery = z.infer<typeof SearchProfilesQuery>;

export const SearchProfilesResponse = z
  .strictObject({
    profiles: z.array(ProfileSummary).meta({
      description:
        "Connected profiles matching the exact id, or claimed usernames matching the prefix. Username matches are ordered by normalized username and then profile id. At most the requested limit.",
    }),
  })
  .meta({
    id: "SearchProfilesResponse",
    description: "Profiles matching the exact id or username prefix.",
    example: {
      profiles: [
        {
          profileId: PROFILE_EXAMPLE.profileId,
          username: PROFILE_EXAMPLE.username,
          displayName: PROFILE_EXAMPLE.displayName,
          avatar: PROFILE_EXAMPLE.avatar,
        },
      ],
    },
  });
export type SearchProfilesResponse = z.infer<typeof SearchProfilesResponse>;

export const PermissionsResponse = z
  .strictObject({
    permissions: z
      .array(Permission)
      .nullable()
      .meta({
        description: "Every permission the person holds, or null when they hold none.",
        example: ["agents.manage", "staff.manage"],
      }),
    isAdmin: z.literal(true).optional().meta({
      description: "Present, and true, only when the person is an admin. Absent for everyone else.",
    }),
  })
  .meta({
    id: "PermissionsResponse",
    description: "One person's staff permissions, for deciding which staff tools to show.",
  });
export type PermissionsResponse = z.infer<typeof PermissionsResponse>;

export const SearchAccountsQuery = PlayerSearchQuery.meta({
  id: "SearchAccountsQuery",
  description: "A small staff search by exact user id, email prefix, or username prefix.",
  example: { q: "ada@", limit: 8 },
});
export type SearchAccountsQuery = z.infer<typeof SearchAccountsQuery>;

export const FoundAccount = z
  .object({
    userId: UserId,
    email: z
      .string()
      .meta({ description: "Their sign-in email, so staff can tell accounts apart." }),
    username: z.string().nullable().meta({
      description: "Their handle, or null while they have not claimed one.",
    }),
    displayName: z.string().min(1),
  })
  .meta({
    id: "FoundAccount",
    description: "One account a staff search found.",
    example: {
      userId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
      email: "ada@example.com",
      username: "ada",
      displayName: "Ada",
    },
  });
export type FoundAccount = z.infer<typeof FoundAccount>;

export const SearchAccountsResponse = z
  .object({
    accounts: z.array(FoundAccount).meta({
      description:
        "The account with the exact id, or accounts whose email or claimed username starts with the text, ordered by that email or username. At most the requested limit.",
    }),
  })
  .meta({ id: "SearchAccountsResponse", description: "Accounts matching a staff search." });
export type SearchAccountsResponse = z.infer<typeof SearchAccountsResponse>;
